import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import { findDOMNode } from "react-dom";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import EmptyState from "../../components/emptyState";
import { withScreenContext } from "../../components/screen/ScreenContext";
import TableView from "../../components/tableView";
import tableViewUtils from "../../components/tableView/utils";
import { GLOBAL_FILTER_ID } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import PubSubTopics from "../../constants/pubSubTopics";
import TableRowTypes from "../../constants/tableRowTypes";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { colorSelectors } from "../../modules/definition/colors";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import displayCacheSelectors from "../../modules/display/selectors";
import dropSelectors from "../../modules/dropSelectors";
import { filterSelectors } from "../../modules/filters";
import { itemSelectors } from "../../modules/items";
import { narrativeOperations, narrativeSelectors } from "../../modules/narrative";
import { timelineReducer } from "../../modules/timelineReducer";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { immutableRecords, PropTypeShapes } from "../../types";
import { selectByPubSubTopics } from "../../types/immutableRecords/model/appState/utils";
import { chooseValueForIos, mergeDispatchToProps } from "../../utils/codeSplit";
import { onPasteItems } from "../../utils/copyPaste";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import { getColumnLimitedWidth } from "../../utils/tableColumns";
import { trackEvent } from "../../utils/trackAnalytics";
import findParentAndIndexOfNode from "../../utils/tree/findParentAndIndexOfNode";
import { appSelectors } from "../app";
import tableViewSelectors from "../sharedSelectors/tableViewSelectors";
import { withWorkspaceContext } from "../workspace/WorkspaceContext";
import selectors from "./selectors";

const tableDataWithoutRowIds = (data, rowIds) => {
  return rowIds && rowIds.length ? data.update("rows", rows => rows.filter(r => rowIds.indexOf(r.id) >= 0)) : data;
};

class OutlineView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      data: immutableRecords.TableRecord({}),
      displayData: immutableRecords.TableRecord({}),
      draggedItemIds: List()
    };
    this._lastAction = null;
    this._tableView = null;
    this.dispatchLocal = this.dispatchLocal.bind(this);
    this.startRowDrag = this.startRowDrag.bind(this);
    this.onMoveRow = this.onMoveRow.bind(this);
    this.internalMoveColumn = this.internalMoveColumn.bind(this);
    this.internalDragLeave = this.internalDragLeave.bind(this);
    this.dispatchLastMoveAction = this.dispatchLastMoveAction.bind(this);
    this.requestCanDropItems = this.requestCanDropItems.bind(this);
    this.onCanDrop = this.onCanDrop.bind(this);
    this.onCanItemDropOnRow = this.onCanItemDropOnRow.bind(this);
    this.onCreateItem = this.onCreateItem.bind(this);
    this.onTableValueChanged = this.onTableValueChanged.bind(this);
    this.onTableMultipleValueChanged = this.onTableMultipleValueChanged.bind(this);
    this.onTableBulkChange = this.onTableBulkChange.bind(this);
    this.onSelectItemByTopicsReceived = this.onSelectItemByTopicsReceived.bind(this);
    this.onScrollToItemReceived = this.onScrollToItemReceived.bind(this);
    this.dragPreviewDataForItemId = this.dragPreviewDataForItemId.bind(this);
    this.onToggleChildren = this.onToggleChildren.bind(this);
    this.onToggleColumn = this.onToggleColumn.bind(this);
    this.onResizeColumn = this.onResizeColumn.bind(this);
    this.getCellOptions = this.getCellOptions.bind(this);
    this.onReceivedFocus = this.onReceivedFocus.bind(this);
    this.onViewClick = this.onViewClick.bind(this);
    this.canTypeHaveSuperType = this.canTypeHaveSuperType.bind(this);

    // paste
    this.onItemPaste = this.onItemPaste.bind(this);
    this.processPastingItems = this.processPastingItems.bind(this);

    this.getItemIdsToExpand = this.getItemIdsToExpand.bind(this);
  }

  componentWillMount() {
    this.copyLocalStore(this.props);
  }

  componentDidMount() {
    this._node = findDOMNode(this);
    this._pubSubScrollToItemToken = PubSub.subscribe(PubSubTopics.scrollToItem, this.onScrollToItemReceived);
    this._pubSubSelectItemByTopicsToken = PubSub.subscribe(
      PubSubTopics.selectItemByTopics,
      this.onSelectItemByTopicsReceived
    );
    this.props.workViewRegistration.registerWorkViewCreateItemCallback(this.props.viewId, this.onCreateItem);
  }

  componentWillReceiveProps(nextProps) {
    this.copyLocalStore(nextProps);
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubScrollToItemToken);
    this.props.workViewRegistration.unregisterWorkViewCreateItemCallback(this.props.viewId);
    PubSub.unsubscribe(this._pubSubSelectItemByTopicsToken);
  }

  onSelectItemByTopicsReceived(msg, selectTopic) {
    const newSelectedIds = selectByPubSubTopics(
      this.props.isActiveView,
      this.state.data.rows.filter(row => row.id !== TableRowTypes.EMPTY),
      selectTopic,
      this.props.selectedItemIds
    );
    if (newSelectedIds.length > 0) {
      this.props.onSelectItems(newSelectedIds);
      this.onScrollToItemReceived("", { id: newSelectedIds[0] });
    }
  }

  onScrollToItemReceived(msg, data) {
    this.scrollToRow(data.id);
  }

  scrollToRow(rowId) {
    if (this._tableView) {
      this._tableView.scrollToRow(rowId);
    }
  }

  canCreateItemOfType(typeId) {
    return typeSelectors.getTypeIsAllowedInNarrative(this.props.localStore, typeId);
  }

  onCreateItem(typeId) {
    const lastSelectedRow = this.state.data.rows.findLast(r => this.props.selectedItemIds.contains(r.id));
    const parentRowId = lastSelectedRow ? lastSelectedRow.parentRowId : null;
    // If this type is not allowed in narrative, then don't add it here.
    if (!this.canCreateItemOfType(typeId)) {
      return false;
    }
    // Otherwise, we can add it.
    // Do we want to add it after current selection?
    if (lastSelectedRow) {
      const narrativePosition = narrativeSelectors.getNarrativePositionForItem(
        narrativeSelectors.getNarrativeTree(this.props.localStore),
        lastSelectedRow.id
      );
      const index = narrativePosition.last() - 1;
      this.props.onCreateItemInNarrative(typeId, parentRowId, index + 1, null, this.props.viewId);
    } else {
      const index = narrativeSelectors.getLastRootIndexForNarrative(this.props.localStore);
      this.props.onCreateItemInNarrative(typeId, null, index, null, this.props.viewId);
    }
    return true;
  }

  onTableValueChanged(itemId, columnId, value) {
    if (itemId !== TableRowTypes.EMPTY) {
      this.props.onValueChanged(itemId, columnId, value, this.props.localStore);
      trackEvent("setItemValueFromOutlineView", "setItemValue", columnId);
      return;
    }
    const typeId = this.props.defaultType;
    // Otherwise, we can add it.
    // Do we want to add it after current selection?
    const index = narrativeSelectors.getLastRootIndexForNarrative(this.props.localStore);
    this.props.onCreateItemInNarrative(typeId, null, index + 1, value, this.props.viewId);
  }

  onTableMultipleValueChanged(itemId, columnId, value, newComponentState) {
    this.props.onMultiValueChanged(itemId, columnId, value, this.props.localStore, newComponentState);
  }

  copyLocalStore(props) {
    const data = selectors.getOutlineViewData(props.localStore, props.viewId);
    const displayData = data;
    this.setState({
      data,
      displayData,
      draggedItemIds: List()
    });
  }

  onTableBulkChange(newRowIds, changes) {
    const defaultTypeId = this.props.defaultType;
    this.props.onBulkChange(newRowIds, changes, defaultTypeId, this.props.localStore);
  }

  dispatchLocal(action, draggedIds) {
    // Dispatch local needs to apply to the original state each time, so we always apply it to props.localStore instead
    const newStore = timelineReducer(this.props.localStore, action, this.props.localStore);
    const data = selectors.getOutlineViewData(newStore, this.props.viewId);
    const displayData = tableDataWithoutRowIds(data, draggedIds);

    this.setState({
      data,
      displayData,
      draggedItemIds: List(draggedIds)
    });
  }

  internalDragLeave() {
    this.copyLocalStore(this.props);
  }

  startRowDrag(itemIds) {
    this.setState({
      draggedItemIds: List(itemIds)
    });
  }

  internalMoveColumn(column, index) {
    const action = appStateOperations.moveOutlineColumn(this.props.viewId, column, index);
    if (_.isEqual(action, this._lastAction)) {
      return;
    }
    this.dispatchLocal(action, []);
    this._lastAction = action;
  }

  requestCanDropItems(itemIds) {
    if (!itemSelectors.getItemsAreAllowedInNarrative(this.props.localStore, itemIds)) {
      return {
        canDrop: false,
        message: interfaceMessages.DRAG_DROP_NARRATIVE_ORDER_NOT_ALLOWED
      };
    }
    return { canDrop: true };
  }

  onMoveRow(itemIds, dropRow, dropAction, fromExternalSource) {
    const { localStore } = this.props;
    let action = dropSelectors.getActionForNarrativeOutlineDrop(localStore, itemIds, dropRow.id, dropAction);
    if (action) {
      // If the drag source was outside this view, then we want to auto-apply filters
      if (fromExternalSource) {
        action = batchOperations.matchFiltersAfterDropAction(action, itemIds, this.props.viewId);
      }

      this.props.onDispatchAction(action);

      // TO DO: Differentiate between group and reorder
      trackEvent("setItemOrderOrGroupByDragFromOutlineView", "setItemOrderOrGroupByDrag");
    }
  }

  onCanDrop(_props, dragItem, _dropTarget) {
    const canDropItems = this.requestCanDropItems(dragItem.multipleItemIds);
    return canDropItems.canDrop;
  }

  onCanItemDropOnRow(_dragItem, dropRow) {
    // With strict setting, only Narrative Folders can have children
    const parentType = dropRow.type;
    return this.props.typesAllowedNarrativeChildren.indexOf(parentType) >= 0;
  }

  onToggleChildren(rowIndex, isExpanded) {
    this.props.onToggleChildren(this.props.viewId, this.state.data.rows.get(rowIndex).id, isExpanded);
  }

  onToggleColumn(column, showColumn) {
    this.props.onToggleColumn(this.props.viewId, column, showColumn);
  }

  onResizeColumn(column, width) {
    const limitedWidth = getColumnLimitedWidth(column.id, width);
    this.props.onResizeColumn(this.props.viewId, column, limitedWidth);
  }

  dispatchLastMoveAction() {
    if (this._lastAction) {
      this.props.onDispatchAction(this._lastAction);
    }
    this.setState({
      displayData: this.state.data,
      draggedItemIds: List()
    });
    this._lastAction = null;
  }

  dragPreviewDataForItemId(itemId) {
    return selectors.getOutlineViewDataForItem(
      this.props.localStore,
      this.props.viewId,
      this.state.displayData,
      itemId,
      this.props.itemsById,
      this.props.allTypesById
    );
  }

  getCellOptions(rowId, columnId) {
    return selectors.getOutlineViewOptionsForColumns(this.props.localStore, rowId, columnId);
  }

  onReceivedFocus() {
    if (!this.props.isActiveView) {
      this.props.onBecameActiveView(this.props.viewId);
    }
  }

  onViewClick(e) {
    const clickWasItem = hasAncestorOfClass(e.target, "tableRow");
    if (!clickWasItem && this.props.selectedItemIds.size > 0) {
      this.props.onViewClick();
    }
  }

  canTypeHaveSuperType(childType, superType) {
    return typeSelectors.canTypeHaveSuperType(this.props.allTypesById, childType, superType);
  }

  processPastingItems(itemMapList) {
    const { rows } = this.state.data;

    const lastSelectedRow = rows.findLast(r => this.props.selectedItemIds.contains(r.id));
    const parentId = lastSelectedRow ? lastSelectedRow.parentRowId : null;

    const index = lastSelectedRow
      ? narrativeSelectors
        .getNarrativePositionForItem(narrativeSelectors.getNarrativeTree(this.props.localStore), lastSelectedRow.id)
        .last()
      : narrativeSelectors.getLastRootIndexForNarrative(this.props.localStore) + 1;

    let extraActions = [];
    itemMapList.forEach(im => {
      extraActions.push(narrativeOperations.moveInNarrative([im.get("id")], parentId, index));
    });

    return extraActions;
  }

  onItemPaste(e) {
    const {
      dispatchPasteActions,
      projectId,
      isActiveView,
      narrativeTypeIds,
      defaultType,
      allTypesById,
      propertiesById,
      colorsById,
      referencesById,
      itemsById
    } = this.props;
    dispatchPasteActions(
      onPasteItems(
        e,
        projectId,
        isActiveView,
        itemMap => this.canCreateItemOfType(itemMap.get("type")),
        this.processPastingItems,
        narrativeTypeIds,
        defaultType,
        typeSelectors.isNarrativeType,
        allTypesById,
        propertiesById,
        colorsById,
        referencesById,
        itemsById
      )
    );
  }

  isNarrativeType(typeId) {
    const type = this.props.allTypesById.get(typeId);
    return typeSelectors.isNarrativeType(type);
  }

  getItemIdsToExpand(itemId) {
    const { itemsById, viewId, onToggleChildren, narrativeTree } = this.props;
    const { rows } = this.state.data;
    let itemIdsToExpand = [];

    // Tries to scroll to narrative items only
    if (this.isNarrativeType(itemsById.get(itemId).type)) {
      const findToExpandSuper = id => {
        const foundParentAndIndex = findParentAndIndexOfNode(narrativeTree, id);
        if (foundParentAndIndex && foundParentAndIndex.parent) {
          const { parent } = foundParentAndIndex;
          itemIdsToExpand.push(parent);
          if (!rows.find(r => r.id === parent)) {
            // The super is not in position (i.e not expanded)
            // find the super of super
            findToExpandSuper(parent);
          }
        }
      };

      findToExpandSuper(itemId);
      if (itemIdsToExpand.length > 0) {
        onToggleChildren(viewId, itemIdsToExpand, true);
      }
    }

    return itemIdsToExpand;
  }

  render() {
    const canRowDrop = true;

    let allowRowDrag = true;
    //#!if iOS === true
// allowRowDrag = this.props.screenContext.isEditMode;
    //#!endif

    const style = {
      display: "flex",
      width: `${this.props.containerRect.width}px`,
      height: `${this.props.containerRect.height}px`
    };

    let emptyState = null;
    if (this.state.data.rows.count() <= 1) {
      //#!if readonly === true
      const isFiltered = this.props.isViewFilterActive || this.props.isGlobalFilterActive;
      emptyState = (
        <EmptyState type="text" svg={false} fixed={true}>
          <div key="content">
            <p>No items to display{isFiltered ? " for this filter" : ""}.</p>
          </div>
        </EmptyState>
      );
      //#!endif
    }

    const lockedColumnCount = tableViewUtils.getStickyColumnsCount(
      this.state.data.columns,
      this.props.containerRect.width,
      this.props.fontSize
    );

    return (
      <div className="outlineView" style={style}>
        <TableView
          ref={tableView => {
            this._tableView = tableView;
          }}
          fontSize={this.props.fontSize}
          data={this.state.displayData}
          lockedColumnCount={lockedColumnCount}
          calendar={this.props.calendar}
          autoAdjustEndDate={this.props.autoAdjustEndDate}
          viewId={this.props.viewId}
          isActiveView={this.props.isActiveView}
          allowColumnSelect={true}
          allowColumnResize={chooseValueForIos(false, true)}
          allowColumnReorder={true}
          allowRowDrag={allowRowDrag}
          allowRowDragNesting={true}
          availableColumns={this.props.availableColumns}
          sortedItemIds={this.props.sortedItemIds}
          selectedRowIds={this.props.selectedItemIds}
          onSelectRows={this.props.onSelectItems}
          onToggleChildren={this.onToggleChildren}
          onAddToSelection={this.props.onAddToSelection}
          onRemoveFromSelection={this.props.onRemoveFromSelection}
          onReceivedFocus={this.onReceivedFocus}
          onViewClick={this.onViewClick}
          onValueChanged={this.onTableValueChanged}
          onMultiValueChanged={this.onTableMultipleValueChanged}
          onBulkChange={this.onTableBulkChange}
          onToggleColumn={this.onToggleColumn}
          onResizeColumn={this.onResizeColumn}
          onLiveColumnDrag={this.internalMoveColumn}
          onColumnDrop={this.dispatchLastMoveAction}
          draggedRowIds={this.state.draggedItemIds}
          onStartRowDrag={this.startRowDrag}
          onRowDrop={this.onMoveRow}
          requestCanDropItems={this.requestCanDropItems}
          // TODO: What does this do?????
          onCanDrop={this.onCanDrop}
          adjustItemEndWhenChangingStart={this.props.adjustItemEndWhenChangingStart}
          canItemDropOnRow={this.onCanItemDropOnRow}
          canTypeHaveSuperType={this.canTypeHaveSuperType}
          canRowDrop={canRowDrop}
          getCellOptions={this.getCellOptions}
          showViolatedConstraints={false}
          //#!if iOS === true
// isEditMode={this.props.screenContext.isEditMode}
          //#!endif
          requestDragPreviewDataForItem={this.dragPreviewDataForItemId}
          onShowInspector={this.props.onShowInspector}
          onItemPaste={this.onItemPaste}
          displayItems={this.props.displayItems}
          getItemIdsToExpand={this.getItemIdsToExpand}
        />
        {emptyState}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    localStore: state.present,
    availableColumns: selectors.getOutlineViewAvailableColumns(state.present, props.viewId),
    narrativeTypeIds: typeSelectors.getNarrativeTypeIds(state.present),
    typesAllowedNarrativeChildren: typeSelectors.getTypeIdsAllowedNarrativeChildren(state.present),
    defaultType: selectors.getOutlineViewDefaultType(state.present, props.viewId),
    calendar: calendarSelectors.getCalendar(state.present),
    autoAdjustEndDate: definitionSelectors.getAdjustItemEndWhenChangingStart(state.present),
    sortedItemIds: selectors.getOutlineViewSortedItemIds(state.present, props.viewId),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    fontSize: appStateSelectors.getFontSize(state.present),
    projectId: appSelectors.getProjectId(state.present),
    allTypesById: definitionSelectors.getTypesById(state.present),
    itemsById: itemSelectors.getItemsById(state.present),
    propertiesById: propertySelectors.getPropertiesById(state.present),
    colorsById: colorSelectors.getColorsById(state.present),
    referencesById: referenceSelectors.getReferencesById(state.present),
    isActiveView: appStateSelectors.isViewActive(state.present, props.viewId),
    displayItems: displayCacheSelectors.getDisplayItemsById(state.present),
    adjustItemEndWhenChangingStart: definitionSelectors.getAdjustItemEndWhenChangingStart(state.present),
    narrativeTree: narrativeSelectors.getNarrativeTree(state.present),
    isGlobalFilterActive: filterSelectors.isFilterWithIdActive(state.present, GLOBAL_FILTER_ID),
    isViewFilterActive: filterSelectors.isFilterWithIdActive(state.present, props.viewId)
  };
};

const readonlyActions = {
  onSelectItems: itemIds => {
    if (itemIds) {
      return appStateOperations.selectItems(itemIds);
    } else {
      return appStateOperations.clearSelection();
    }
  },
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onToggleChildren: appStateOperations.toggleExpandSuperItem,
  onViewClick: appStateOperations.clearSelection,
  onToggleColumn: appStateOperations.toggleOutlineColumn,
  onResizeColumn: appStateOperations.resizeOutlineColumn,
  onBecameActiveView: appStateOperations.setActiveView,
  onShowInspector: uiStateOperations.showInspectorModal
};

const editingActions = {
  onCreateItemInNarrative: (typeId, parent, index, label, viewId) => {
    trackEvent("createItemFromOutlineView", "createItem", typeId);
    let selectItem = true;
    //#!if iOS === true
// selectItem = false;
    //#!endif
    return batchOperations.matchFiltersAfterCreateAction(
      batchOperations.createItemInNarrativeWithLabel(typeId, parent, index, label, selectItem),
      viewId
    );
  },
  onValueChanged: (itemId, columnId, value, globalState) => {
    return tableViewSelectors.createActionForValueChange(itemId, columnId, value, globalState);
  },
  onMultiValueChanged: tableViewSelectors.createActionForValueChange,
  onBulkChange: (newRowIds, changes, typeId, globalState) => {
    return tableViewSelectors.createActionForBulkChange(newRowIds, changes, typeId, globalState);
  },
  onMoveNarrativeContainer: batchOperations.moveNarrativeContainer,
  onMoveNarrativeLeaves: narrativeOperations.moveLeaves,
  onDispatchAction: a => a,
  dispatchPasteActions: actions => {
    return batchOperations.reBatchActions(actions);
  },
  onShowInspector: appStateOperations.showRightPanel
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

OutlineView.propTypes = {
  viewId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  localStore: PropTypes.object.isRequired,
  availableColumns: PropTypeShapes.availableTableColumnList,
  calendar: PropTypeShapes.calendar.isRequired,
  autoAdjustEndDate: PropTypes.bool,
  narrativeTree: PropTypes.object.isRequired,
  defaultType: PropTypes.string.isRequired,
  narrativeTypeIds: ImmutablePropTypes.list.isRequired,
  typesAllowedNarrativeChildren: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onToggleChildren: PropTypes.func.isRequired,
  onViewClick: PropTypes.func.isRequired,
  fontSize: PropTypes.number.isRequired,
  onCreateItemInNarrative: PropTypes.func.isRequired,
  onValueChanged: PropTypes.func.isRequired,
  onMultiValueChanged: PropTypes.func.isRequired,
  onBulkChange: PropTypes.func.isRequired,
  onToggleColumn: PropTypes.func.isRequired,
  onResizeColumn: PropTypes.func.isRequired,
  onBecameActiveView: PropTypes.func.isRequired,
  onDispatchAction: PropTypes.func.isRequired,
  allTypesById: ImmutablePropTypes.map.isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  displayItems: ImmutablePropTypes.map.isRequired,
  adjustItemEndWhenChangingStart: PropTypes.bool.isRequired,
  isViewFilterActive: PropTypes.bool,
  isGlobalFilterActive: PropTypes.bool,

  // copy paste
  dispatchPasteActions: PropTypes.func,
  propertiesById: ImmutablePropTypes.map,
  colorsById: ImmutablePropTypes.map,
  referencesById: ImmutablePropTypes.map,

  onShowInspector: PropTypes.func,

  // Workspace context
  workViewRegistration: PropTypes.object.isRequired,

  // Comes from ContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

const normalView = withContainerContext(
  withWorkspaceContext(connect(mapStateToProps, mapDispatchToProps)(OutlineView))
);
let exportedView = normalView;

//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default exportedView;
